import React, { useEffect, useRef, useState } from 'react';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { validatePlaca } from '../../utils/validators';
import { normalizePlaca } from '../../utils/formatters';
import './SearchForm.css';

declare global {
  interface Window {
    grecaptcha?: {
      render: (
        container: string | HTMLElement,
        params: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          size?: 'normal' | 'compact' | 'invisible';
        }
      ) => string | number;
      reset: (widgetId?: string | number) => void;
    };
  }
}

interface SearchFormProps {
  onSearch: (placa: string, captchaToken: string, resetCaptcha: () => void) => void;
  loading?: boolean;
  disabled?: boolean;
}

const SearchForm: React.FC<SearchFormProps> = ({ onSearch, loading = false, disabled = false }) => {
  const [placa, setPlaca] = useState('');
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [captchaError, setCaptchaError] = useState<string>('');
  const captchaContainerRef = useRef<HTMLDivElement | null>(null);
  const captchaWidgetIdRef = useRef<string | number | null>(null);

  const resetCaptcha = () => {
    setCaptchaToken('');
    setCaptchaError('');
    if (window.grecaptcha && captchaWidgetIdRef.current != null) {
      window.grecaptcha.reset(captchaWidgetIdRef.current);
    }
  };

  useEffect(() => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    if (!siteKey) {
      setCaptchaError('Captcha no configurado');
      return;
    }

    const tryRender = () => {
      if (!captchaContainerRef.current) return;
      if (!window.grecaptcha || typeof window.grecaptcha.render !== 'function') return;
      if (captchaWidgetIdRef.current != null) return;

      captchaWidgetIdRef.current = window.grecaptcha.render(captchaContainerRef.current, {
        sitekey: siteKey,
        callback: (token: string) => {
          setCaptchaError('');
          setCaptchaToken(token);
        },
        'expired-callback': () => {
          setCaptchaToken('');
        },
        'error-callback': () => {
          setCaptchaToken('');
          setCaptchaError('Error al validar captcha');
        },
      });
    };

    tryRender();

    const intervalId = window.setInterval(() => {
      tryRender();
      if (captchaWidgetIdRef.current != null) {
        window.clearInterval(intervalId);
      }
    }, 250);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar placa
    const validation = validatePlaca(placa);
    if (!validation.isValid) {
      setError(validation.error || 'Placa inválida');
      return;
    }

    // Limpiar error y buscar
    setError('');
    const placaNormalizada = normalizePlaca(placa);

    if (!captchaToken) {
      setCaptchaError('Completa el captcha para continuar');
      return;
    }

    onSearch(placaNormalizada, captchaToken, resetCaptcha);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPlaca(e.target.value);
    if (error) setError(''); // Limpiar error al escribir
    if (captchaError) setCaptchaError('');
  };

  return (
    <form className="search-form" onSubmit={handleSubmit}>
      <div className="search-form-content">
        <Input
          type="text"
          placeholder="Ej: ABC123"
          value={placa}
          onChange={handleChange}
          error={error}
          fullWidth
          icon={<span>🔍</span>}
          disabled={loading || disabled}
        />
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={loading}
          disabled={loading || disabled || !placa.trim() || !captchaToken}
        >
          Consultar
        </Button>
      </div>

      <div className="search-form-captcha">
        <div ref={captchaContainerRef} />
        {captchaError && <p className="search-form-captcha-error">{captchaError}</p>}
      </div>

      <p className="search-form-hint">
        Ingrese la placa del vehículo para consultar su estado de sistematización
      </p>
    </form>
  );
};

export default SearchForm;
